import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import {
  CalendarDays,
  Download,
  FolderOpen,
  Gauge,
  Globe,
  Grid2X2Plus,
  LogOut,
  Monitor,
  MoreHorizontal,
  Plug,
  RefreshCw,
  Settings,
  SquareKanban,
  UserRound,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { useLayoutEffect, useState } from "react";
import { useSidebarUpdate } from "./DesktopUpdates";
import { IconTooltip } from "./IconTooltip";

type Props = {
  mini?: boolean;
  compactActions?: (close: () => void) => ReactNode;
  name: string;
  initials: string | null;
  image?: string | null;
  email?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIntegrations: () => void;
  onBoard?: () => void;
  onSchedule?: () => void;
  onArtifacts: () => void;
  onSites?: () => void;
  onRooms?: () => void;
  onSettings: () => void;
  /** Opens the computers paired through HrokBot Connect; left out where the server has none. */
  onRemoteComputers?: () => void;
  /** Paired computers active right now; null while unknown. */
  remoteComputersActive?: number | null;
  onUpdates?: () => void;
  onUsage: () => void;
  onLogOut: () => void;
  signingOut: boolean;
  logOutError: string | null;
};

export function SidebarFooter({
  mini = false,
  compactActions,
  name,
  initials,
  image,
  email,
  open,
  onOpenChange,
  onIntegrations,
  onBoard,
  onSchedule,
  onArtifacts,
  onSites,
  onRooms,
  onSettings,
  onRemoteComputers,
  remoteComputersActive = null,
  onUpdates,
  onUsage,
  onLogOut,
  signingOut,
  logOutError,
}: Props) {
  const { t } = useLingui();
  const update = useSidebarUpdate();
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const [workOpen, setWorkOpen] = useState(false);
  useLayoutEffect(() => {
    setWorkOpen(false);
  }, [mini]);
  const workItems = [
    ...(onRooms ? [{ label: t`Shared rooms`, icon: Users, action: onRooms }] : []),
    ...(onSites ? [{ label: t`Sites`, icon: Globe, action: onSites }] : []),
    { label: t`Files`, icon: FolderOpen, action: onArtifacts },
    ...(onSchedule ? [{ label: t`Bot schedule`, icon: CalendarDays, action: onSchedule }] : []),
    ...(onBoard ? [{ label: t`Tasks`, icon: SquareKanban, action: onBoard }] : []),
  ];
  return (
    <div
      data-testid="sidebar-footer"
      className={`flex shrink-0 flex-col gap-1 bg-transparent ${mini ? "items-center px-1 py-1" : "px-2.5 py-2"}`}
    >
      {mini ? (
        <Popover
          open={workOpen}
          onOpenChange={(next) => {
            setWorkOpen(next);
            if (next) onOpenChange(false);
          }}
        >
          <IconTooltip label={t`More`}>
            <PopoverTrigger
              aria-label={t`More`}
              data-testid="sidebar-work-menu"
              className="app-no-drag grid size-11 shrink-0 place-items-center rounded-xl text-muted-foreground outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreHorizontal aria-hidden="true" size={20} />
            </PopoverTrigger>
          </IconTooltip>
          <PopoverContent
            side={document.documentElement.dir === "rtl" ? "left" : "right"}
            sideOffset={8}
            align="end"
            className="app-no-drag max-h-(--available-height) w-72 max-w-[calc(100vw-5rem)] overflow-y-auto p-1 [&_[data-testid=bot-create-picker]]:max-w-full"
          >
            <nav aria-label={t`More`} className="flex flex-col">
              {workOpen && compactActions ? (
                <>
                  {compactActions(() => setWorkOpen(false))}
                  <hr className="my-1 border-border" />
                </>
              ) : null}
              {workItems.map(({ label, icon: Icon, action }) => (
                <Button
                  key={label}
                  variant="ghost"
                  aria-label={label}
                  className="min-h-11 w-full justify-start gap-3 rounded-xl font-normal"
                  onClick={() => {
                    setWorkOpen(false);
                    action();
                  }}
                >
                  <Icon
                    aria-hidden="true"
                    size={18}
                    className="shrink-0 text-muted-foreground"
                    strokeWidth={1.75}
                  />
                  {label}
                </Button>
              ))}
            </nav>
          </PopoverContent>
        </Popover>
      ) : (
        <nav
          aria-label={t`Your work`}
          data-testid="sidebar-work-toolbar"
          className="flex min-h-11 items-center justify-around gap-1"
        >
          {workItems.map(({ label, icon: Icon, action }) => (
            <IconTooltip key={label} label={label}>
              <Button
                variant="ghost"
                aria-label={label}
                onClick={action}
                className="size-11 min-h-11 shrink-0 rounded-xl"
              >
                <Icon
                  aria-hidden="true"
                  size={18}
                  className="shrink-0 text-muted-foreground"
                  strokeWidth={1.75}
                />
              </Button>
            </IconTooltip>
          ))}
        </nav>
      )}
      <div className={`flex min-w-0 items-center gap-2 ${mini ? "flex-col" : "w-full"}`}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            if (next) setWorkOpen(false);
            onOpenChange(next);
          }}
        >
          <PopoverTrigger
            data-testid="user-menu-trigger"
            aria-label={t`Account menu for ${name}`}
            className={`flex min-h-11 min-w-0 items-center gap-2.5 rounded-full text-sm font-medium text-foreground outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring ${mini ? "size-11 shrink-0" : "flex-1 pe-2 text-start"}`}
          >
            <span className="grid size-11 shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-sidebar-accent">
              {image && failedImage !== image ? (
                <img
                  src={image}
                  alt=""
                  className="size-full object-cover"
                  onError={() => setFailedImage(image)}
                />
              ) : initials ? (
                initials
              ) : (
                <UserRound size={18} aria-hidden="true" />
              )}
            </span>
            {!mini ? <span className="min-w-0 truncate text-base font-normal">{name}</span> : null}
          </PopoverTrigger>
          {open ? (
            <PopoverContent
              side="top"
              sideOffset={8}
              align="start"
              className="max-h-(--available-height) w-[295px] max-w-[calc(100vw-69px)] gap-0 overflow-y-auto p-1 *:shrink-0 data-closed:animate-none"
            >
              <div
                data-testid="account-menu-header"
                className="mb-1 min-w-0 border-b border-border px-2.5 py-2.5"
              >
                <p className="truncate text-sm font-medium">{name}</p>
                {email && email !== name ? (
                  <p className="truncate text-xs text-muted-foreground">{email}</p>
                ) : null}
              </div>
              {mini ? (
                <Button
                  variant="ghost"
                  className="min-h-11 w-full justify-start gap-2.5 text-base font-normal"
                  onClick={onIntegrations}
                >
                  <Plug className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Connect apps</Trans>
                </Button>
              ) : null}
              <Button
                variant="ghost"
                className="min-h-11 w-full justify-start gap-2.5 text-base font-normal"
                onClick={onUsage}
              >
                <Gauge className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Usage</Trans>
              </Button>
              <Button
                variant="ghost"
                className="min-h-11 w-full justify-start gap-2.5 text-base font-normal"
                aria-label={t`Settings`}
                onClick={onSettings}
              >
                <Settings className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Settings</Trans>
              </Button>
              {onRemoteComputers ? (
                <Button
                  variant="ghost"
                  className="min-h-11 w-full justify-start gap-2.5 text-base font-normal"
                  onClick={onRemoteComputers}
                >
                  <Monitor className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Remote Computer</Trans>
                  {remoteComputersActive !== null ? (
                    <span
                      className="ml-auto text-sm tabular-nums text-muted-foreground"
                      data-testid="remote-computers-active"
                    >
                      {remoteComputersActive}
                    </span>
                  ) : null}
                </Button>
              ) : null}
              {update.supported && onUpdates ? (
                <Button
                  variant="ghost"
                  className="min-h-11 w-full justify-start gap-2.5 text-base font-normal"
                  onClick={() => {
                    onUpdates();
                    update.check();
                  }}
                >
                  <RefreshCw className="text-muted-foreground" strokeWidth={1.75} />
                  <Trans>Check for updates</Trans>
                </Button>
              ) : null}
              <div className="my-1 border-t border-border" />
              <Button
                variant="ghost"
                className="min-h-11 w-full justify-start gap-2.5 text-base font-normal"
                disabled={signingOut}
                onClick={onLogOut}
              >
                <LogOut className="text-muted-foreground" strokeWidth={1.75} />
                <Trans>Log out</Trans>
              </Button>
              {logOutError ? (
                <p role="alert" className="px-2.5 pt-1 pb-2 text-base text-destructive">
                  {logOutError}
                </p>
              ) : null}
            </PopoverContent>
          ) : null}
        </Popover>
        {!mini ? (
          <IconTooltip label={t`Connect apps`}>
            <Button
              variant="outline"
              size="icon"
              aria-label={t`Connect apps`}
              onClick={onIntegrations}
              className="size-11 min-h-11 shrink-0 rounded-full bg-sidebar-accent text-muted-foreground transition-[transform,background-color,border-color,color] hover:border-foreground/30 hover:bg-sidebar-accent hover:text-foreground active:scale-[0.96] motion-reduce:transition-none dark:hover:bg-sidebar-accent"
            >
              <Grid2X2Plus aria-hidden="true" className="size-5" strokeWidth={1.75} />
            </Button>
          </IconTooltip>
        ) : null}
        {update.available ? (
          <IconTooltip label={t`Install update`}>
            <Button
              size="icon-lg"
              className="rounded-full"
              aria-label={t`Install update`}
              disabled={update.busy}
              onClick={update.install}
            >
              <Download aria-hidden="true" />
            </Button>
          </IconTooltip>
        ) : null}
      </div>
    </div>
  );
}
